import React, { ReactNode, useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  StyleProp,
  StyleSheet,
  useWindowDimensions,
  ViewStyle,
} from 'react-native';

/**
 * Slides its children in from the right edge when they mount.
 *
 * This app has no navigator, so a screen is a boolean on `HomeScreen` or a
 * stage on `App` - the tree is swapped and the new page simply appears. That
 * reads as a redraw rather than as movement, and gives no sense of having gone
 * anywhere. A push from the right is the convention every stack navigator uses
 * and the one people already read as "forward".
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS NOT
 * ---------------------------------------------------------------------------
 * It animates the arrival only. There is no exit: the branch it wraps stops
 * rendering the instant its flag flips, so there is nothing left to animate
 * out - the same reason `BottomSheet` has to keep itself mounted through its
 * own close. A real transition in both directions needs the outgoing screen
 * held alive, which means a navigator.
 *
 * Deliberately not applied to a screen someone returns *to*. Home is the root;
 * sliding it in when the cart closes would say you had gone forward into it.
 */

/*
 * 240 read as a flash - the page seemed to appear rather than arrive, which
 * on a screen with no exit animation is disorienting. 340 is still quicker
 * than the stock navigator push (~350–400) but slow enough to be seen.
 */
const SLIDE_MS = 340;

type Props = {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
};

function SlideIn({ children, style }: Props) {
  const { width } = useWindowDimensions();

  /** 1 is off to the right, 0 is in place. */
  const slide = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const run = Animated.timing(slide, {
      toValue: 0,
      duration: SLIDE_MS,
      // Fast to start and settling at the end, so it arrives rather than
      // coasting in.
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    run.start();
    return () => run.stop();
  }, [slide]);

  return (
    <Animated.View
      style={[
        styles.fill,
        style,
        {
          transform: [
            {
              // Interpolated from the measured width rather than animating the
              // offset directly, so a rotation mid-slide lands in the right
              // place instead of stopping short of the edge.
              translateX: slide.interpolate({
                inputRange: [0, 1],
                outputRange: [0, width],
              }),
            },
          ],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
});

export default SlideIn;
